import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { css } from '@emotion/react'
import { mobile, tablet, InsetSection } from '~styles/global'
import DataViewer from '~utils/DataViewer'
import { graphql } from 'gatsby'
import PageHeader from '~components/PageHeader'
import RichText from '~components/RichText'
import { SlideDown } from 'react-slidedown'
import 'react-slidedown/lib/slidedown.css'
import Button from '~components/Button'
import { Plus } from '~components/Svg'
import RenderModules from '~components/RenderModules'
import { useSiteState } from '~context/siteContext'
import Image from '~components/Image'
import { Link } from 'gatsby'
import resolveLink from '~utils/resolveLink'
import Seo from '~components/Seo'
import useSafari from '~utils/useSafari'

const Project = ({ data }) => {
	const page = data?.sanityProject
	const [readMore, setReadMore] = useState(false)
	const [siteState, setSiteState] = useSiteState()
	const safari = useSafari()

	const projects = data?.allSanityProject.nodes
	function lexographicalSort(a, b) {
		if (a['orderRank'] < b['orderRank']) {
			return -1
		}
		if (a['orderRank'] > b['orderRank']) {
			return 1
		}
		return 0
	}
	projects.sort(lexographicalSort)
	const currentProjectIndex = projects.findIndex((project) => {
		return project.title === page.title
	})

	useEffect(() => {
		setSiteState((prevState) => ({
			...prevState,
			pageTitle: page.title,
		}))
	}, [])

	return (
		<>
			<Seo title={page.title} metaTitle={page.seo?.metaTitle} description={page.seo?.metaDescription} image={page.seo?.socialImage} />
			<Wrap>
				<StyledPageHeader content={page?.headerMedia} />
				<Content>
					<Intro>
						<IntroText>
							<RichText content={page.introText} />
							<SlideDown>
								{readMore && (
									<IntroTextExtended>
										<IntroTextExtendedInner>
											<RichText content={page?.introTextExtended} />
										</IntroTextExtendedInner>
									</IntroTextExtended>
								)}
							</SlideDown>
							{page?.introTextExtended && (
								<ReadMore onClick={() => setReadMore(!readMore)} readMore={readMore}>
									Read More <StyledPlus />
								</ReadMore>
							)}
						</IntroText>
						<Meta className="sans-sm">
							<RichText content={page?.projectDetails} />
						</Meta>
					</Intro>
					<RenderModules modules={page?.modules} />
					{projects[currentProjectIndex + 1] && (
						<NextProject to={resolveLink(projects[currentProjectIndex + 1])}>
							{!safari && (
								<NextProjectContent>
									<NextProjectTitleContainer className="h2">
										Next project
										<NextProjectTitle>{projects[currentProjectIndex + 1]?.title}</NextProjectTitle>
									</NextProjectTitleContainer>
								</NextProjectContent>
							)}
							<NextProjectContentOverlay>
								<NextProjectTitleContainer className="h2">
									Next project
									<NextProjectTitle>{projects[currentProjectIndex + 1]?.title}</NextProjectTitle>
								</NextProjectTitleContainer>
							</NextProjectContentOverlay>
							<NextProjectContentOverlay2>
								<NextProjectTitleContainer className="h2">
									Next project
									<NextProjectTitle>{projects[currentProjectIndex + 1]?.title}</NextProjectTitle>
								</NextProjectTitleContainer>
							</NextProjectContentOverlay2>
							{projects[currentProjectIndex + 1]?.headerMedia?.image && (
								<NextProjectImage image={projects[currentProjectIndex + 1].headerMedia.image} cover />
							)}
						</NextProject>
					)}
				</Content>
				<DataViewer data={{ ...page, ...projects }} name="page" />
			</Wrap>
		</>
	)
}

const Wrap = styled.div``

const StyledPageHeader = styled(PageHeader)`
	/* pointer-events: none; */
`

const Intro = styled(InsetSection)`
	> div {
		padding-top: 66px;
		margin: 0 0 66px;
	}
	${mobile} {
		> div {
			padding-top: 46px;
			margin: 0 0 46px;
		}
	}
`
const IntroText = styled.div`
	grid-column: 1/6;
	${tablet} {
		grid-column: span 12;
	}
`
const ReadMore = styled(Button)`
	margin-top: ${(props) => (props.readMore ? '28px' : '18px')};
	transition: margin 0.2s;
`
const IntroTextExtended = styled.div`
	box-sizing: border-box;
`
const IntroTextExtendedInner = styled.div`
	padding: 0;
`
const Meta = styled.div`
	grid-column: span 6/13;
	${tablet} {
		grid-column: span 12;
	}
	${mobile} {
		margin-top: 46px;
	}
`
const StyledPlus = styled(Plus)`
	width: 16px;
	color: var(--pink);
	display: inline-block;
	margin-left: 19px;
`
const NextProject = styled(Link)`
	position: relative;
	display: block;
	background: black;
	min-height: 100vh;
`
const NextProjectImage = styled(Image)`
	height: 100vh;
`
const NextProjectContent = styled(InsetSection)`
	position: absolute;
	top: 0;
	bottom: 0;
	left: 0;
	right: 0;
	z-index: 1;
	mix-blend-mode: color;
	> div {
		margin: 26px 0;
		position: sticky;
		top: 28px;
	}
	${mobile} {
		> div {
			top: 22px;
			margin: 15px 0;
		}
	}
`
const NextProjectContentOverlay = styled(NextProjectContent)`
	mix-blend-mode: exclusion;
	z-index: 2;
`
const NextProjectContentOverlay2 = styled(NextProjectContent)`
	mix-blend-mode: overlay;
	opacity: 0.5;
	z-index: 3;
`
const NextProjectTitleContainer = styled.div`
	grid-column: span 6;
	color: var(--white);
	font-size: 32px;
	line-height: 1.04;
	${mobile} {
		grid-column: span 12;
		top: 23px;
		font-size: 27px;
	}
`
const NextProjectTitle = styled.div`
	margin-top: 3px;
`
const Content = styled.div`
	background-color: var(--black);
	color: var(--white);
`
Project.propTypes = {
	data: PropTypes.object,
}

export const query = graphql`
	query ProjectQuery($slug: String) {
		sanityProject(slug: {current: {eq: $slug}}) {
			title
			slug {
				current
			}
			seo {
				...seo
			}
			projectName
			headerMedia {
				...headerMedia
			}
			introText: _rawIntroText(resolveReferences: {maxDepth: 5})
			introTextExtended: _rawIntroTextExtended(resolveReferences: {maxDepth: 5})
			projectDetails: _rawProjectDetails(resolveReferences: {maxDepth: 5})
			modules {
				... on SanityMediaSlider {
					...mediaSlider
				}
				... on SanityTextSection {
					_type
					_key
					text: _rawText(resolveReferences: {maxDepth: 5})
				}
				... on SanityVideoSection {
					_type
					_key
					showControls
					video {
						...videoEmbed
					}
					backgroundColor
				}
				... on SanityVideo {
					_type
					_key
					vimeoUrl {
						oEmbedData {
							video_id
							width
							height
						}
					}
				}
				... on SanityVimeoInline {
					_type
					_key
					vimeoUrl {
						oEmbedData {
							video_id
							width
							height
						}
					}
					showControls
				}
				... on SanityImageSection {
					_type
					_key
					image {
						...imageWithAlt
					}
					backgroundColor
					size
				}
			}
		}
		allSanityProject {
			nodes {
				headerMedia {
					...headerMedia
				}
				slug {
					current
				}
				title
				orderRank
				_type
			}
		}
	}
`

export default Project
